import React, { FunctionComponent, useMemo } from "react";

import { DATE_FORMATS, DateTime } from "@bps/date-time";
import {
  IColumn,
  Link,
  ScrollablePane,
  Stack,
  TooltipHost
} from "@bps/fluent-ui";
import { ScrollListener } from "@components/tables/ScrollListener";
import { ShimmeredDetailsList } from "@components/tables/ShimmeredDetailsList";
import { BpIdUserSignInLog } from "@libs/api/gateways/bp-id/bp-id.dtos";
import { useUserSignInLogs } from "@libs/api/gateways/bp-id/bp-id.hooks";
import { config } from "@libs/config/config";
import { getPagesResult } from "@libs/paging/paging.utils";

interface UserSignInLogsListProps {
  userId?: string;
  startDate?: Date | undefined;
  endDate?: Date | undefined;
}
export const UserSignInLogsList: FunctionComponent<UserSignInLogsListProps> = ({
  userId,
  startDate,
  endDate
}) => {
  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage
  } = useUserSignInLogs({ userId, startDate, endDate });

  const items = useMemo(() => getPagesResult(data), [data]);

  const columns: IColumn[] = [
    {
      key: "userPrincipalName",
      name: "Sign-in Email",
      maxWidth: 240,
      minWidth: 190,
      onRender: (item: BpIdUserSignInLog) => item.userPrincipalName
    },
    {
      key: "userDisplayName",
      name: "Bp Id User Name",
      maxWidth: 170,
      minWidth: 140,
      onRender: (item: BpIdUserSignInLog) => item.userDisplayName
    },
    {
      key: "createdDateTime",
      name: "On",
      maxWidth: 180,
      minWidth: 150,
      onRender: (item: BpIdUserSignInLog) =>
        item.createdDateTime
          ? DateTime.fromISO(item.createdDateTime).toFormat(
              DATE_FORMATS.LONG_DATE_TIME_FORMAT
            )
          : "-"
    },
    {
      key: "ipAddress",
      name: "IP Address",
      maxWidth: 120,
      minWidth: 100,
      onRender: (item: BpIdUserSignInLog) => item.ipAddress
    },
    {
      key: "errorCode",
      name: "Error Code",
      maxWidth: 100,
      minWidth: 80,
      onRender: (item: BpIdUserSignInLog) => item.status.errorCode
    },
    {
      key: "failureReason",
      name: "Failure Reason",
      maxWidth: 500,
      minWidth: 400,
      onRender: (item: BpIdUserSignInLog) => (
        <TooltipHost content={item.status.additionalDetails}>
          {item.status.failureReason}
        </TooltipHost>
      )
    },
    {
      key: "seq",
      name: "B2C Correlation Id",
      maxWidth: 100,
      minWidth: 80,
      onRender: (item: BpIdUserSignInLog) => {
        if (item.createdDateTime && item.correlationId) {
          const from = DateTime.fromISO(item.createdDateTime)
            .setZone("utc")
            ?.minus({ minute: 2 })
            ?.toISO();

          const to = DateTime.fromISO(item.createdDateTime)
            .setZone("utc")
            ?.plus({ minute: 2 })
            ?.toISO();
          return (
            <Link
              href={`${config.seqUrl}?filter=B2CCorrelationId%20%3D%20'${item.correlationId}'&from=${from}&to=${to}`}
              target="_blank"
              rel="noreferrer"
            >
              {item.correlationId}
            </Link>
          );
        }
      }
    }
  ];

  const handleScrolledToBottom = async () => {
    if (hasNextPage) {
      await fetchNextPage();
    }
  };

  return (
    <Stack styles={{ root: { position: "relative", height: 800 } }}>
      <ScrollablePane>
        <ShimmeredDetailsList
          items={items}
          enableShimmer={isLoading}
          errorMessage={error?.message}
          columns={columns}
          stickyHeader
        />
        <ScrollListener
          hasNextPage={true}
          onScrolledToBottom={handleScrolledToBottom}
        />
      </ScrollablePane>
    </Stack>
  );
};
